import { getNodeTagsMap } from "@features/administration/products/EditProduct/nodeTagsHelper"
import { useGraph } from "@features/builder/graph/graphAtoms"
import { useNodesValidation } from "@features/builder/graph/nodes/validation/useNodesValidation"
import { BulkNodeTagsFieldRow } from "@features/builder/BulkNodesEditDialog/BulkNodesEditForm/BulkNodeTagsFieldRow"
import { BulkNodesEditFormValues } from "@features/builder/BulkNodesEditDialog/BulkNodesEditForm/BulkNodesEditForm"
import { Add } from "@mui/icons-material"
import { FormHelperText, Grid, IconButton, Tooltip } from "@mui/material"
import { useGetNodeTagsQuery } from "@store/services/productsService"
import { CUSTOM_ERROR_ATTRIBUTE } from "@utils/form"
import { useMemo } from "react"
import { useFieldArray, useFormContext, useFormState } from "react-hook-form"

type NodeTagsCellProps = {
  nodeId: string
}

const NodeTagsCell = (props: NodeTagsCellProps) => {
  const { graphId } = useGraph()
  const form = useFormContext<BulkNodesEditFormValues>()
  const tagsPath = `nodes.${props.nodeId}.tags` as const
  const { fields, append, remove } = useFieldArray({ control: form.control, name: tagsPath })
  const { errors } = useFormState({ control: form.control, name: tagsPath })
  const errorMessage = errors.nodes?.[props.nodeId]?.tags?.root?.message ?? ""

  const { getNodeValidation } = useNodesValidation()
  const validationErrors = getNodeValidation(props.nodeId, "NODE_TAGS")?.errors ?? []

  const [organisationKey, divisionKey, productKey] = (graphId ?? "").split("#")
  const { data: getTagsResponse } = useGetNodeTagsQuery(
    {
      key: productKey ?? "",
      orgKey: organisationKey ?? "",
      divKey: divisionKey ?? "",
    },
    { skip: !productKey || !organisationKey || !divisionKey },
  )
  const tagsMap = useMemo(() => getNodeTagsMap(getTagsResponse ?? ""), [getTagsResponse])

  return (
    <Grid container direction="column" spacing={1} wrap="nowrap">
      {fields.map((tagField, index) => (
        <BulkNodeTagsFieldRow
          key={tagField.id}
          nodeId={props.nodeId}
          index={index}
          onDelete={() => remove(index)}
          tagsMap={tagsMap}
          validationErrors={validationErrors}
        />
      ))}

      {errorMessage && (
        <Grid item>
          <FormHelperText error {...{ [CUSTOM_ERROR_ATTRIBUTE]: true }}>
            {errorMessage}
          </FormHelperText>
        </Grid>
      )}

      <Grid item>
        <Tooltip title="Add Tag">
          <span>
            <IconButton
              color="primary"
              size="small"
              disabled={form.formState.disabled}
              onClick={() => append({ tag: "", value: "" })}
            >
              <Add />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
    </Grid>
  )
}

export default NodeTagsCell
